import React, { useEffect, useMemo, useState } from 'react';

import AppButton from '../../../../commons/buttons/AppButton';
import AppLinkButton from '../../../../commons/buttons/AppLinkButton';
import AppLoading from '../../../../commons/AppLoading';
import { Badge } from 'react-bootstrap';
import HallAppToast from '../../../../commons/HallAppToast';
import SchedulingAPI from '../../SchedulingAPI';
import { useParams } from 'react-router-dom';

// Utils simples para montar link do WhatsApp
const onlyDigits = (s = '') => (s || '').replace(/\D/g, '');
const buildWaLink = ({ phoneE164, text }) => {
    // phoneE164: ex. 5511999999999 (com DDI)
    const p = onlyDigits(phoneE164 || '');
    const msg = encodeURIComponent(text || '');
    return p ? `https://wa.me/${p}?text=${msg}` : `https://wa.me/?text=${msg}`;
};

const formatDateLong = (input) => {
    try {
        let dt;

        // dd/MM/yyyy
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(input)) {
            const [d, m, y] = input.split('/').map(Number);
            dt = new Date(y, m - 1, d);
        }
        // yyyy-MM-dd (ISO sem hora)
        else if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
            const [y, m, d] = input.split('-').map(Number);
            dt = new Date(y, m - 1, d);
        }
        // ISO completo com hora
        else {
            dt = new Date(input);
        }

        if (isNaN(dt.getTime())) return input;

        return dt.toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });
    } catch {
        return input;
    }
};


const schedulingAPI = new SchedulingAPI();

const ConfirmScheduling = () => {
    const [status, setStatus] = useState('pending'); // 'pending' | 'confirming' | 'confirmed' | 'cancelling' | 'cancelled' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [scheduling, setScheduling] = useState(null);

    const { token } = useParams();

    useEffect(() => {
        getScheduling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getScheduling = async () => {
        try {
            const resp = await schedulingAPI.getSchedulingByToken(token);
            setScheduling(resp.data);
            setIsLoading(false);
            const status = resp.data?.status
            const responseDate = resp.data?.responseDate

            if (status === 'ACTIVE' && !responseDate) {
                setStatus('pending')
            } else if (status === 'ACTIVE' && responseDate) {
                setStatus('confirmed')
            }  else if (status === 'INACTIVE') {
                setStatus('cancelled')
            }
        } catch (_) {
            setStatus('error');
            setIsLoading(false);
        }
    };

    const totalServices = scheduling?.services?.length || 0;

    const handleConfirm = async () => {
        try {
            setStatus('confirming')
            setIsLoading(true);
            await schedulingAPI.updateScheduling(token, 'ACTIVE');
            getScheduling();
        } catch (e) {
            HallAppToast.genericError();
            setStatus('error');
            setErrorMsg('Não foi possível confirmar. Tente novamente.');
            setIsLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            setStatus('cancelling')
            setIsLoading(true);
            await schedulingAPI.updateScheduling(token, 'INACTIVE');
            getScheduling();
        } catch (e) {
            HallAppToast.genericError();
            setStatus('error');
            setErrorMsg('Não foi possível cancelar. Tente novamente.');
            setIsLoading(false);
        }
    };

    const disabledActions =
        status === 'confirming' ||
        status === 'cancelling' ||
        status === 'confirmed' ||
        status === 'cancelled';

    // Dados para contato/links
    // Tente pegar do scheduling; senão, defina fallback.
    const establishmentWhatsE164 = scheduling?.companyNumber
    const customerName = scheduling?.customerName || '';
    const dateLong = formatDateLong(scheduling?.date);
    const waText = `Olá, sou ${customerName || 'cliente'}. Sobre meu agendamento em ${dateLong}, poderia me ajudar?`;

    const waHref = buildWaLink({
        phoneE164: establishmentWhatsE164,
        text: waText,
    });

    const instagramUrl = scheduling?.instagramUrl

    return (
        <div className="confirm-page">
            {isLoading ? (
                <AppLoading />
            ) : (
                <div className="confirm-card">
                    <header className="confirm-header">
                        <h2 className="confirm-title">
                            {scheduling?.customerName
                                ? `Olá, ${scheduling?.customerName}!`
                                : 'Confirmação de Agendamento'}
                        </h2>

                        {status === 'confirmed' && <Badge bg="success">Confirmado</Badge>}
                        {status === 'cancelled' && <Badge bg="secondary">Cancelado</Badge>}
                        {status === 'pending' && <Badge bg="warning">Pendente</Badge>}
                        {status === 'confirming' && <Badge bg="info">Confirmando…</Badge>}
                        {status === 'cancelling' && <Badge bg="info">Cancelando…</Badge>}
                        {status === 'error' && <Badge bg="danger">Erro</Badge>}
                    </header>

                    <section className="confirm-section">
                        <div className="confirm-block">
                            <div className="confirm-label">Data</div>
                            <div className="confirm-value">{dateLong}</div>
                        </div>

                        <div className="confirm-block">
                            <div className="confirm-label">Endereço</div>
                            <div className="confirm-value">{scheduling?.address}</div>
                        </div>

                        <div className="confirm-block">
                            <div className="confirm-label">Serviços ({totalServices})</div>
                            <ul className="confirm-services">
                                {scheduling?.services?.map((s, idx) => (
                                    <li key={`${s.time}-${idx}`} className="confirm-service-item">
                                        <span className="svc-time">⏰ {s.time}</span>
                                        <span className="svc-name">{s.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    {errorMsg && <p className="confirm-error">{errorMsg}</p>}

                    <footer className="confirm-actions">
                        <AppButton
                            name={status === 'confirmed' ? 'Confirmado' : 'Confirmar presença'}
                            size="md"
                            onClick={handleConfirm}
                            disabled={disabledActions}
                        />
                        <AppLinkButton
                            name={status === 'cancelled' ? 'Cancelado' : 'Cancelar agendamento'}
                            onClick={handleCancel}
                            disabled={disabledActions}
                        />
                    </footer>

                    {/* CTAs extras */}
                    <section className="contact-block">
                        <h3 className="contact-title">Precisa falar com o estabelecimento?</h3>
                        <div className="contact-actions">
                            <a
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="wa-contact"
                                aria-label="Falar com o estabelecimento no WhatsApp"
                            >
                                💬 Falar no WhatsApp
                            </a>

                            {
                                instagramUrl && <a
                                    href={`https://instagram.com/${instagramUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ig-contact"
                                    aria-label="Visitar o Instagram do estabelecimento"
                                >
                                    📷 Siga no Instagram
                                </a>
                            }
                        </div>
                        {!establishmentWhatsE164 && (
                            <p className="wa-hint">
                                Dica: o número do WhatsApp do estabelecimento não está cadastrado. Cadastre para
                                facilitar o contato.
                            </p>
                        )}
                    </section>

                    {(status === 'confirmed' || status === 'cancelled') && (
                        <div className="confirm-feedback">
                            {status === 'confirmed' ? (
                                <p>Pronto! Seu horário está confirmado. Nos vemos na data informada.</p>
                            ) : (
                                <p>
                                    Seu agendamento foi cancelado. Se mudou de ideia, você pode reagendar a qualquer
                                    momento.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            <style jsx>{`
        .confirm-page {
          min-height: 100dvh;
          padding: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fc;
        }
        .confirm-card {
          width: 100%;
          max-width: 720px;
          background: #fff;
          border: 1px solid #eee;
          border-radius: 14px;
          padding: 20px;
        }
        .confirm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .confirm-title {
          margin: 0;
          font-size: 20px;
          line-height: 1.3;
        }
        .confirm-section {
          margin-top: 16px;
          display: grid;
          gap: 14px;
        }
        .confirm-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }
        .confirm-value { color: #111827; }
        .confirm-services {
          list-style: none;
          padding: 0;
          margin: 6px 0 0;
          display: grid;
          gap: 8px;
        }
        .confirm-service-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: 1px solid #eee;
          border-radius: 10px;
          background: #fafafa;
        }
        .svc-time { font-weight: 600; color: #4f46e5; min-width: 72px; }
        .svc-name { color: #111827; }

        .confirm-error { margin: 12px 0 0; color: #e11d48; }

        .confirm-actions {
          margin-top: 18px;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .contact-block {
          margin-top: 22px;
          border-top: 1px dashed #e5e7eb;
          padding-top: 16px;
        }
        .contact-title { margin: 0 0 10px; font-size: 16px; }
        .contact-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .wa-contact, .ig-contact {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
        }
        .wa-contact {
          background: #25d366;
          color: #fff;
        }
        .wa-contact:hover { filter: brightness(0.95); }
        .ig-contact {
          background: #f1f5f9;
          color: #111827;
        }
        .ig-contact:hover { background: #e8eef5; }

        .wa-hint { margin-top: 8px; color: #6b7280; font-size: 13px; }

        .confirm-feedback { margin-top: 12px; color: #374151; }

        @media (max-width: 480px) {
          .confirm-card { padding: 16px; }
          .svc-time { min-width: 64px; }
        }
      `}</style>
        </div>
    );
};

export default ConfirmScheduling;